/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { PageQueryParams, SearchQueryParams } from '../apiCommon.typings';
import { CashBoxesListPageQueryParams, CashBoxesStatus, CashBoxTypes } from '../cashBoxes/cashBoxesClient.typings';

import { DeviceStatus, DeviceUSBType, DeviceWorkability } from '../devices/devicesClient.typing';
import { ServiceOperations } from '../../../components/Service/ServiceTabs/ServiceTab/serviceTabConfig';

//-----cashbox services-----------------------------------------------------------------------------------

export enum CashBoxTaskType {
    CHECKSUM = 'CHECKSUM',
    LOGS = 'LOGS',
    ENVINFO = 'ENVINFO',
    FWUPDATE = 'FWUPDATE',
    CERTUPDATE = 'CERTUPDATE',
    DUMP = 'DUMP',
    REPAIR = 'REPAIR',
}

export enum CashBoxTaskStatus {
    CREATED = 'CREATED',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    REJECTED = 'REJECTED', //made by frontend
}

export interface CashBoxTaskDetails {
    targetVersion?: string;
    allowWithDocumentUnsent?: boolean;
    allowWithShiftOpened?: boolean;
    adminPIN?: string;
    newAdminPIN?: string;
    newAdminPUK?: string;
    newUserPIN?: string;
    newUserPUK?: string;
}

export interface CashBoxTaskResults {
    currentVersion?: string;
    checksum?: string;
    osName?: string;
    osVersion?: string;
    osArch?: string;
    procCount?: number;
    ramSizeMB?: number;
    utcOffsetSec?: number;
    fdVersion?: string;
}

export interface CashBoxTaskItemDto {
    cashBoxTaxNumber: string;
    id: string;
    name: CashBoxTaskType;
    status: CashBoxTaskStatus;
    insertedAtUtc: string;
    updatedAtUtc: string;
    userName: string;
    statusCode?: number;
    statusDetails?: string;
    details?: CashBoxTaskDetails;
    results?: CashBoxTaskResults;
}

export interface CashBoxTasksListDto {
    count: number;
    items: CashBoxTaskItemDto[];
}

export interface CashBoxTasksListRequestParams extends PageQueryParams {
    cashBoxTaxNumbers: string[];
    latest?: boolean;
    name?: CashBoxTaskType;
    status?: CashBoxTaskStatus;
}

export interface CreateCashBoxTaskRequestParams {
    cashBoxTaxNumber: string;
    name: CashBoxTaskType;
    details?: CashBoxTaskDetails;
}

export interface CreateCashBoxTaskResponseDto {
    id: string;
    key: string;
    cashBoxTaxNumber: string;
    errorMessage?: string;
}

export interface CreateCashBoxTaskResponse {
    id: string;
    cashBoxTaxNumber: string;
    taskName: CashBoxTaskType;
    errorMessage?: string;
}

export enum DevicePlatform {
    LPC = 'lpc55s28',
    STM = 'stm32f4',
}

export enum DevicePlatformOfMultiItems {
    LPC = 'lpc55s28',
    STM = 'stm32f4',
    none = 'none',
    both = 'both',
}

export interface SuitableTargetVersionsParams {
    devicePlatform: DevicePlatform;
    currentVersion: string;
}

export interface SuitableTargetVersionsDto {
    versions: string[];
}

export interface SuitableTargetVersionsForDevicePlatform extends SuitableTargetVersionsDto {
    devicePlatform: DevicePlatform;
}

export interface ServiceCashboxesListItem extends RoughCashboxesListItemDto {
    selected: boolean;
    certificate: Partial<CashBoxTaskItemDto>;
    firmware: Partial<CashBoxTaskItemDto>;
    logs: Partial<CashBoxTaskItemDto>;
    envInfo: Partial<CashBoxTaskItemDto>;
    dump: Partial<CashBoxTaskItemDto>;
    repair: Partial<CashBoxTaskItemDto>;
}

export interface RoughCashboxesListItemDto {
    taxNumber: string;
    deviceFactoryNumber: string;
    cashRegisterFactoryNumber: string;
    firmwareVersion: string;
    fiscalDriverVersion: string;

    status: CashBoxesStatus;
    cashBoxType: CashBoxTypes;
    online: boolean;
    cashRegisterModelName: string;
    cashRegisterModelTaxNumber: string;
    softwareVersion: string;

    operatorName: string;
    operatorTaxNumber: string;
    companyTaxNumber: string;
    storeTaxNumber: string;
}

export interface RoughCashboxesListDto {
    afterKeyTaxNumber: string;
    cashBoxes: RoughCashboxesListItemDto[];
}

export interface RoughCashboxesListFilterParams extends Partial<SearchQueryParams> {
    online: boolean;
    cashBoxTypes: CashBoxTypes[];
    firmwareVersions?: string[];
    fiscalDriverVersions?: string[];

    operatorTaxNumbers?: string[];
    cashRegisterModelTaxNumbers?: string[];

    cashBoxStatuses?: CashBoxesStatus[];
}

export type RoughCashboxesListRequestParams = CashBoxesListPageQueryParams & RoughCashboxesListFilterParams;

export interface RoughCashboxesListData {
    isLoading: boolean;
    cashboxes: RoughCashboxesListItemDto[];
    filterParams: RoughCashboxesListFilterParams;
    changedFilterParams: RoughCashboxesListFilterParams;
    limit: number;
    afterKeyTaxNumber: string;
    lastRequestItemsCount: number;
}

//-----device services-----------------------------------------------------------------------------------

export enum InitialisationOperations {
    PRODUCE = 'PRODUCE',
    RESERVE = 'RESERVE',
    UNRESERVE = 'UNRESERVE',
    USB_TYPE = 'USB_TYPE',
}

export interface DeviceServiceListItem {
    factoryNumber: string;
    status: DeviceStatus;
    statusDate: number;
    statusComment: string;
    usbType: DeviceUSBType;
    firmwareVersion: string;
    hardwareVersion: string;
    wearout: number;
    workability: DeviceWorkability;
    operatorTaxNumber?: string;
    cashBoxTaxNumber?: string;
}

export interface UploadInitializationFileBody {
    file: File;
    operation: InitialisationOperations;
}

export interface ServiceOperationBody {
    operationType: ServiceOperations;
    factoryNumbers: string[];
}
